<template>
  <div class="industry-market page-shell">
    <div class="toolbar">
      <span class="toolbar-label">所属行业</span>
      <el-select
        v-model="selectedIndustry"
        size="small"
        filterable
        clearable
        placeholder="全部行业"
        style="width: 220px"
      >
        <el-option
          v-for="item in industries"
          :key="item.name"
          :label="`${item.name}（${item.count}）`"
          :value="item.name"
        />
      </el-select>

      <span class="toolbar-label">区间</span>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        :picker-options="pickerOptions"
      />

      <el-button type="primary" size="small" :loading="loading" @click="onQueryClick">
        查询
      </el-button>
    </div>

    <el-alert
      v-if="showDbLagWarning"
      type="warning"
      :closable="false"
      show-icon
      class="sync-alert"
    >
      <template slot="title">
        库内最新交易日为 {{ importStatus.dbMaxDate }}，所选结束日 {{ dateRange[1] }} 将部分使用 CSV 回退。
        <el-button type="text" size="mini" :loading="syncing" @click="syncCsv">同步至数据库</el-button>
      </template>
    </el-alert>

    <el-alert
      v-if="summary?.adjusted"
      type="info"
      :closable="false"
      show-icon
      class="sync-alert"
    >
      <template slot="title">
        已对齐至最近交易日：{{ summary.start }} → {{ summary.end }}
        （原选择 {{ summary.requestedStart }} → {{ summary.requestedEnd }}）
      </template>
    </el-alert>

    <div v-if="summary" class="stats-bar">
      <span>
        {{ summary.industry }} · {{ summary.start }} → {{ summary.end }}
      </span>
      <span>
        可计算 {{ summary.computable }}/{{ summary.filterCount }}
        · 下跌 {{ summary.down }} / 上涨 {{ summary.up }}
      </span>
      <span v-if="summary.avg != null">
        平均 {{ formatPct(summary.avg) }}% · 中位 {{ formatPct(summary.median) }}%
      </span>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" class="error-alert" />

    <div class="table-shell" v-loading="loading" element-loading-text="查询中...">
      <el-table
        v-if="total > 0"
        ref="industryTable"
        :data="rows"
        style="width: 100%"
        :height="tableHeight"
        :default-sort="{ prop: sortBy, order: tableSortOrder }"
        :row-style="{ height: '32px', lineHeight: '32px' }"
        :cell-style="{ padding: '2px 0' }"
        @sort-change="onSortChange"
      >
      <el-table-column prop="declineRank" label="降幅排名" width="90" sortable="custom" />
      <el-table-column prop="mvRank" label="市值排名" width="90" sortable="custom" />
      <el-table-column prop="code" label="代码" width="100" sortable="custom">
        <template slot-scope="{ row }">
          <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">
            {{ row.code }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="名称" width="120">
        <template slot-scope="{ row }">
          <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">
            {{ row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column prop="industry" label="所属行业" min-width="120" />
      <el-table-column :label="`${startLabel}价`" prop="priceStart" width="100" sortable="custom">
        <template slot-scope="{ row }">{{ formatPrice(row.priceStart) }}</template>
      </el-table-column>
      <el-table-column :label="`${endLabel}价`" prop="priceEnd" width="100" sortable="custom">
        <template slot-scope="{ row }">{{ formatPrice(row.priceEnd) }}</template>
      </el-table-column>
      <el-table-column prop="changeAmt" label="涨跌额" width="100" sortable="custom">
        <template slot-scope="{ row }">{{ formatPrice(row.changeAmt) }}</template>
      </el-table-column>
      <el-table-column prop="changePct" label="区间涨跌幅(%)" width="130" sortable="custom">
        <template slot-scope="{ row }">
          <span :class="pctClass(row.changePct)">{{ formatPct(row.changePct) }}%</span>
        </template>
      </el-table-column>
      <el-table-column prop="marketValue" label="持股市值(亿元)" width="130" sortable="custom" />
      <el-table-column prop="shares" label="持有基金家数" width="120" sortable="custom" />
      <el-table-column prop="action" label="持股变化" min-width="100" />
      <el-table-column prop="fundUrl" label="相关链接" min-width="100">
        <template slot-scope="{ row }">
          <el-link
            v-if="row.fundUrl"
            :href="benefitFundLink(row.fundUrl)"
            target="_blank"
            rel="noopener noreferrer"
          >
            受益基金
          </el-link>
        </template>
      </el-table-column>
    </el-table>
    </div>

    <el-pagination
      v-if="total > 0"
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :current-page.sync="page"
      :page-size.sync="pageSize"
      :page-sizes="pageSizeOptions"
      :total="total"
      @current-change="onPageChange"
      @size-change="onPageSizeChange"
    />

    <div v-else-if="queried && !loading && !error" class="empty-tip">无数据，请确认行业与日期区间</div>
  </div>
</template>

<script>
import { fetchIndustries, fetchIndustryMarket } from '../utils/industryApi.js'
import { fetchImportStatus, syncCsvToDb } from '../utils/importApi.js'
import { stockstarCodeLink, eastmoneyCodeLink, benefitFundLink } from '../utils/stockLink.js'

const DEFAULT_START_DATE = '2026-06-30'

function formatDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function todayDate() {
  return formatDate(new Date())
}

function shortDate(dateStr) {
  const [, m, d] = dateStr.split('-')
  return `${m}-${d}`
}

export default {
  name: 'IndustryMarket',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const end = todayDate()
    return {
      industries: [],
      selectedIndustry: '',
      dateRange: [DEFAULT_START_DATE, end],
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      pageSizeOptions: [10, 20, 50, 100],
      sortBy: 'changePct',
      sortOrder: 'asc',
      summary: null,
      startLabel: shortDate(DEFAULT_START_DATE),
      endLabel: shortDate(end),
      loading: false,
      queried: false,
      error: '',
      importStatus: null,
      syncing: false,
      tableHeight: 560,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        },
      },
    }
  },
  computed: {
    tableSortOrder() {
      return this.sortOrder === 'desc' ? 'descending' : 'ascending'
    },
    showDbLagWarning() {
      if (!this.importStatus?.dbMaxDate || !this.dateRange?.[1]) return false
      return this.dateRange[1] > this.importStatus.dbMaxDate
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.updateTableHeight()
      }
    },
    summary() {
      this.updateTableHeight()
    },
    error() {
      this.updateTableHeight()
    },
  },
  async created() {
    await Promise.all([this.loadIndustries(), this.loadImportStatus()])
    await this.queryMarketIfReady()
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$el?.querySelector('.table-shell')
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const pagerHeight = this.total > 0 ? 48 : 0
        const height = Math.max(240, Math.floor(window.innerHeight - top - pagerHeight - 20))
        if (height !== this.tableHeight) {
          this.tableHeight = height
        }
        this.$nextTick(() => {
          const table = this.$refs.industryTable
          if (table?.doLayout) table.doLayout()
        })
      })
    },
    stockstarCodeLink,
    eastmoneyCodeLink,
    benefitFundLink,
    async loadImportStatus() {
      try {
        this.importStatus = await fetchImportStatus()
      } catch {
        this.importStatus = null
      }
    },
    async syncCsv() {
      this.syncing = true
      try {
        const result = await syncCsvToDb()
        this.$message.success(`已同步 ${result.ok} 个交易日`)
        await this.loadImportStatus()
      } catch (err) {
        this.$message.error(err.message || '同步失败')
        await this.loadImportStatus()
      } finally {
        this.syncing = false
      }
    },
    async loadIndustries() {
      try {
        this.industries = await fetchIndustries()
        if (!this.selectedIndustry && this.industries.length) {
          this.selectedIndustry = this.industries[0].name
        }
      } catch (err) {
        this.error = err.message || '加载行业列表失败'
      }
    },
    queryMarketIfReady() {
      if (!this.dateRange || this.dateRange.length !== 2) return
      return this.queryMarket()
    },
    onQueryClick() {
      this.page = 1
      this.queryMarket()
    },
    onPageChange() {
      this.queryMarket()
    },
    onPageSizeChange() {
      this.page = 1
      this.queryMarket()
    },
    onSortChange({ prop, order }) {
      if (!order) {
        this.sortBy = 'changePct'
        this.sortOrder = 'asc'
      } else {
        this.sortBy = prop || 'changePct'
        this.sortOrder = order === 'descending' ? 'desc' : 'asc'
      }
      this.page = 1
      this.queryMarket()
    },
    async queryMarket() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择日期区间')
        return
      }

      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const data = await fetchIndustryMarket({
          industry: this.selectedIndustry || '',
          start: this.dateRange[0],
          end: this.dateRange[1],
          page: this.page,
          pageSize: this.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
        })
        this.rows = data.list || []
        this.total = data.total || 0
        this.page = data.page || this.page
        this.pageSize = data.pageSize || this.pageSize
        this.sortBy = data.sortBy || this.sortBy
        this.sortOrder = data.sortOrder || this.sortOrder
        this.startLabel = data.startLabel
        this.endLabel = data.endLabel
        this.summary = {
          industry: data.industry,
          requestedStart: data.requestedStart,
          requestedEnd: data.requestedEnd,
          start: data.start,
          end: data.end,
          adjusted: data.adjusted,
          filterCount: data.filterCount,
          computable: data.computable,
          down: data.down,
          up: data.up,
          avg: data.avg,
          median: data.median,
        }
        this.updateTableHeight()
      } catch (err) {
        this.rows = []
        this.total = 0
        this.summary = null
        this.error = err.message || '查询失败'
      } finally {
        this.loading = false
      }
    },
    formatPrice(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : ''
    },
    formatPct(v) {
      if (v == null || v === '') return ''
      return Number(v).toFixed(2)
    },
    pctClass(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return ''
      return n > 0 ? 'pct-up' : 'pct-down'
    },
  },
}
</script>

<style scoped>
.industry-market {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.stats-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.sync-alert {
  margin-bottom: 12px;
}

.error-alert {
  margin-bottom: 12px;
}

.empty-tip {
  padding: 24px 0;
  color: #909399;
  text-align: center;
}

.pct-up {
  color: #f56c6c;
}

.pct-down {
  color: #67c23a;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.table-shell {
  min-height: 0;
}
</style>
